import * as React from 'react';
import { isNil } from 'lodash';
import { Form, Button, Select, Space, theme, Tag } from 'antd';
import { DeleteOutlined, EditOutlined, HolderOutlined, WarningOutlined } from '@ant-design/icons';
import { type UniqueIdentifier } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import * as Styles from './styled';

const { useToken } = theme;

export interface SortableStepProps {
  field: {
    key: number;
    name: number;
    fieldKey?: number;
  };
  label: React.ReactNode;
  disabled?: boolean;
  deleteDisabled?: boolean;
  value?: string;
  options?: GetProp<typeof Select, 'options'>;
  selectProps?: Omit<GetProps<typeof Select>, 'options' | 'value' | 'onChange' | 'defaultValue'>;
  onChange?: (value: string) => void;
  onDelete?: (index: number | number[]) => void;
  onEditClick?: (value: string) => void;
}

const StepSelect = ({
  field,
  label,
  value,
  options,
  disabled,
  deleteDisabled,
  selectProps,
  onChange,
  onDelete,
  onEditClick,
}: SortableStepProps) => {
  const { status } = Form.Item.useStatus();
  const { token } = useToken();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.key as UniqueIdentifier,
  });

  const containerStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    width: '100%',
    opacity: isDragging ? 0.6 : 1,
  };

  const compactStyle: React.CSSProperties = {
    borderRadius: token.borderRadiusXS,
    border: `1px solid ${disabled || status === 'error' ? token.colorError : token.colorBorder}`,
  };

  const selectLabelRender = React.useCallback<GetProp<typeof Select, 'labelRender'>>(
    (p) => {
      if (options?.find((opt) => opt.value === p.value)) return p.label;

      return <Tag color="red">Event Deleted</Tag>;
    },
    [options],
  );

  return (
    <Styles.StepSpace ref={setNodeRef} style={containerStyle} size={8}>
      {!disabled ? (
        <Button
          {...attributes}
          {...listeners}
          type="text"
          icon={<HolderOutlined />}
          data-draggable
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        />
      ) : (
        <div style={{ width: 26 }} />
      )}
      <Space.Compact block style={compactStyle}>
        <Button data-step-label disabled={disabled} type="text">
          {label}
        </Button>
        <Select
          {...selectProps}
          value={value}
          options={options}
          labelRender={selectLabelRender}
          disabled={disabled}
          placeholder="Select event"
          showSearch
          variant="borderless"
          optionFilterProp="label"
          style={{ flex: 1 }}
          onChange={onChange}
        />
        <Button
          variant="text"
          color={disabled || status === 'error' ? 'danger' : 'default'}
          disabled={deleteDisabled}
          icon={<DeleteOutlined />}
          onClick={() => onDelete?.(field.name)}
        />
      </Space.Compact>
      {disabled ? (
        <WarningOutlined style={{ fontSize: 24, padding: '0 5px', color: token.colorError }} />
      ) : (
        <Button
          disabled={isNil(value)}
          icon={<EditOutlined />}
          style={{ height: 34, width: 34 }}
          onClick={() => value && onEditClick?.(value)}
        />
      )}
    </Styles.StepSpace>
  );
};

export default React.memo(StepSelect);
